import React from "react";
import { Paperclip, FileText, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";

const ModalPostular1 = ({
  show,
  onClose,
  onNext,
  tender,
  formData,
  setFormData,
}) => {
  const { propuesta, archivosAdjuntos } = formData; // ✅ Extraer desde formData

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "pdf",
    "docx",
    "doc",
    "xlsx",
    "xls",
    "csv",
    "txt",
    "pptx",
    "ppt",
    "zip",
    "rar",
    "7z",
  ];

  const handleInputChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      propuesta: value, // ✅ Guardar directamente en formData
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return (
        ALLOWED_EXTENSIONS.includes(fileExtension) && file.size <= MAX_FILE_SIZE
      );
    });

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        archivosAdjuntos: [
          ...(prev.archivosAdjuntos || []),
          ...validFiles,
        ].slice(0, 3), // ✅ Asegurar que siempre es un array
      }));
    }
  };

  const handleFileRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      archivosAdjuntos: (prev.archivosAdjuntos || []).filter(
        (_, i) => i !== index
      ), // ✅ Asegurar que es un array antes de filtrar
    }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    onNext(); // ✅ No es necesario pasar datos, ya están en formData
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/2 bg-primary" />
        </div>
        <DialogHeader className="mt-4">
          <DialogTitle>Postular a la Licitación</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">Título: </strong>
          {tender?.titulo
            ? tender.titulo.length > 40
              ? `${tender.titulo.substring(0, 40)}...`
              : tender.titulo
            : "Sin título"}
        </p>

        {/* Mostrar archivos adjuntos si existen */}
        {tender?.files?.length > 0 && (
          <div className="mt-2 text-sm">
            <strong className="text-foreground">Descarga los archivos adjuntados por el licitador:</strong>
            <ul className="mt-1 space-y-1">
              {tender.files.map((file, index) => (
                <li key={index}>
                  <a
                    href={file.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" /> {file.file_name || `Archivo ${index + 1}`}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleNext} className="mt-4 flex flex-col gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Propuesta</label>
            <textarea
              id="propuesta"
              name="propuesta"
              value={propuesta}
              onChange={handleInputChange}
              placeholder="Describe tu propuesta detalladamente. Esta información la leerá tu cliente."
              minLength={15}
              maxLength={1000}
              required
              rows={5}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            <small className="text-xs text-muted-foreground">{`${propuesta.length}/1000 caracteres`}</small>
          </div>

          {/* Sección de subida de archivos */}
          <div>
            <strong className="text-sm text-foreground">Adjuntar archivos (Máx: 3 archivos, 10 MB c/u)</strong>
            <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                className="hidden"
              />
              <Paperclip className="h-4 w-4" /> Seleccionar archivos
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              Formatos permitidos: {ALLOWED_EXTENSIONS.join(", ")}
            </p>

            {/* Lista de archivos seleccionados */}
            <ul className="mt-2 space-y-1">
              {archivosAdjuntos?.map((file, index) => (
                <li key={index} className="flex items-center justify-between gap-2 text-sm">
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" /> {file.name}
                  </a>
                  <button
                    type="button"
                    onClick={() => handleFileRemove(index)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">Siguiente</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalPostular1;
